import { getJwtToken } from '../config/cognito'
import { ApiError } from '../config/api'

// Mirror the trailing-slash strip applied in config/api.ts so paths that
// start with `/api/...` never produce a `//api/...` double-slash, regardless
// of how the env-sync pipeline formats `REACT_APP_API_URL`.
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')

/**
 * The PDF-template payload the server produces from `wps_sections` (minus
 * the raw image Buffer the PDF uses; the canvas fetches the weld-pass
 * sequence diagram via its signed `imageUrl` instead).
 *
 * Shape mirrors `PqrTemplateData` in `server/src/pdf/pqrDataMapper.ts` —
 * keeping a structural copy here avoids a build-time cross-package
 * dependency. Any change to the server shape must be mirrored here.
 */
export interface PqrPreviewIdentifiers {
  pqrNumber: string
  revision: string
  sheetOf: string
}

export interface PqrPreviewCompany {
  company: string
  contractor: string
  project: string
  industrySpec: string
  companySpec: string
  crcWeldFileNo: string
}

export interface PqrPreviewMaterialRow {
  label: string
  materialSpec: string
  typeOrGrade: string
  nominalThicknessIn: string
  nominalDiameterIn: string
  manufacturer: string
  heatNo: string
  /**
   * Multi-weld PQRs (Phase 1 of `multi-weld-pqr`): identifies which weld
   * this row belongs to. Absent for legacy / aggregated rows.
   */
  weldId?: string
  /** Optional human weldNumber prefix for the stacked render. */
  weldNumber?: string
}

export interface PqrPreviewWeldingVariables {
  passes: string[]
  rows: Array<{ label: string; unit?: string; values: string[] }>
}

export interface PqrPreviewJointPreparationEntry {
  /** When present, marks this entry as belonging to a specific weld scope. */
  weldId?: string
  weldNumber?: string
  bevelSvg: string
  jointType: string
  dimensions: Array<{ key: string; label: string; value: string }>
  /** Equix block under bevel; always present (— when blank). */
  maxJointMisalignment?: string
  seamLocations?: { upstream: string; downstream: string }
  /**
   * Signed URL for the admin-uploaded bevel diagram (resolved against the
   * active bevel's joint type). Empty when the admin has not uploaded one;
   * the canvas then falls back to the generic SVG sketch.
   */
  imageUrl: string
}

/** Legacy alias kept for any consumer importing the old name. */
export type PqrPreviewJointPreparation = PqrPreviewJointPreparationEntry

export interface PqrPreviewWeldingParameters {
  passes: string[]
  columns: Array<{ key: string; header: string; unit: string }>
  rows: Array<{ pass: string; cells: Array<{ min: string; max: string }> }>
}

export interface PqrPreviewWeldPassSequenceEntry {
  weldId?: string
  weldNumber?: string
  rootType: 'internal' | 'external' | ''
  capCount: number | null
  fillCount: number | null
  imageUrl: string
}

export type PqrPreviewWeldPassSequence = PqrPreviewWeldPassSequenceEntry

export interface PqrPreviewWelderGroup {
  weldId?: string
  weldNumber?: string
  rows: Array<{ role: string; name: string }>
}

export interface PqrPreviewNotes {
  welders: Array<{ role: string; name: string }>
  equipment: Array<{ label: string; value: string }>
  /** CRC Weld Completion Times band — always three slots when present. */
  weldCompletionTimes?: Array<{ label: string; value: string }>
}

export interface PqrPreviewSignatorySlot {
  name: string
  title?: string
  date?: string
  signatureImage?: string
  stampImage?: string
}

export interface PqrPreviewSignatures {
  preparedBy: PqrPreviewSignatorySlot
  reviewedBy: PqrPreviewSignatorySlot
}

/** Floor Sheet page payload (mirrors server `FloorSheetTemplateData`). */
export interface FloorSheetPreviewData {
  weldId: string
  /** Same PQR No. shown on the PQR TopBand. */
  pqrNumber: string
  procedureTitle: string
  revision: string
  sheetOf: string
  pupNumber: string
  locations: string[]
  preheat: {
    unit: string
    rows: Array<{ pass: string; values: Record<string, string>; min: string; max: string }>
  }
  iwmHeads: Array<{
    head: number
    cw: { amps: string; volts: string; gas: string; wfs: string }
    ccw: { amps: string; volts: string; gas: string; wfs: string }
  }>
  misfire: {
    powerSupply: string
    polarity: string
    process: string
    progression: string
    wireFeedSpeed: string
    completedBy: string
    cells: {
      CW: Record<string, { amps1: string; amps2: string; volts1: string; volts2: string; arcTime: string; length: string; travel: string }>
      CCW: Record<string, { amps1: string; amps2: string; volts1: string; volts2: string; arcTime: string; length: string; travel: string }>
    }
  }
  showIwmHeads: boolean
  showMisfire: boolean
  hasRootMisfirePass: boolean
  deposition: {
    unit: string
    calculatedTopOfGroove: string
    afterRoot: Record<string, string>
    afterRootAvg: string
    afterCaps: Record<string, string>
    afterCapsAvg: string
    byPass: Array<{ pass: string; values: Record<string, string>; avg: string }>
  }
  bevel: {
    unit: string
    pupOne: string
    pupTwo: string
    rows: Array<{ label: string; pupOne: Record<string, string>; pupTwo: Record<string, string> }>
  }
  topOfBevel: Record<string, string>
  targets: { travelSpeed: string; wfs: string; amps: string; volts: string; gas: string }
  targetValues: {
    calculatedTopOfGroove: string
    fillAngleA: string
    shelfHeight: string
    landThickness: string
    offsetE: string
  }
  miscellaneous: {
    maxJointMisalignment: string
    afterRootAvg: string
    afterCapsAvg: string
  }
  bevelImageUrl: string
  recordedBy: { name: string; title?: string; date?: string; signatureImage?: string; stampImage?: string }
  /** 1..N Recorded By cards; falls back to `[recordedBy]` when omitted by older payloads. */
  recordedByList?: Array<{
    name: string
    title?: string
    date?: string
    signatureImage?: string
    stampImage?: string
  }>
  /** Optional Reviewed By card on the Floor Sheet footer. */
  reviewedBy?: {
    name: string
    title?: string
    date?: string
    signatureImage?: string
    stampImage?: string
  }
  shopFloorComments: string
  generatedAt: string
  projectName: string
}

export interface PqrPreviewTemplateData {
  identifiers: PqrPreviewIdentifiers
  company: PqrPreviewCompany
  materials: PqrPreviewMaterialRow[]
  weldingVariables: PqrPreviewWeldingVariables
  /** Single panel — first weld in multi-weld mode (back-compat). */
  jointPreparation: PqrPreviewJointPreparationEntry
  /** Multi-weld stacked entries. Present only when `weldIds.length > 1`. */
  jointPreparations?: PqrPreviewJointPreparationEntry[]
  weldingParameters: PqrPreviewWeldingParameters
  /** Single panel — first weld in multi-weld mode (back-compat). */
  weldPassSequence: PqrPreviewWeldPassSequenceEntry
  /** Multi-weld stacked entries. Present only when `weldIds.length > 1`. */
  weldPassSequences?: PqrPreviewWeldPassSequenceEntry[]
  notes: PqrPreviewNotes
  /** Multi-weld welder groups. Present only when `weldIds.length > 1`. */
  weldersByWeld?: PqrPreviewWelderGroup[]
  accompanyingReports: Array<{ label: string; value: string }>
  comments: string
  signatures: PqrPreviewSignatures
  generatedAt: string
}

export interface PqrPreviewResponse {
  /** Full `PqrTemplateData` payload (sans binary). */
  data: PqrPreviewTemplateData
  /** Floor Sheet page(s) that accompany the PQR PDF. */
  floorSheets: FloorSheetPreviewData[]
  /** `wps_sections.version` at the time of the read. */
  version: number
}

/**
 * Fetch the same payload the PDF renderer consumes, so the preview canvas
 * can render the PDF layout 1:1. Uses a hand-rolled fetch (rather than
 * `apiFetch`) because we want to surface the `ETag` header — the server
 * sends `"<version>"` as ETag and we want it to coincide with the JSON
 * `version` field for cross-checking / optimistic-concurrency reuse.
 *
 * Passing a non-empty `weldId` scopes the response to that single weld
 * (Materials / Joint Prep / Pass Sequence / Welding Parameters / Welders).
 * Omitted or empty = legacy aggregated view; a stale/unknown weldId is
 * silently downgraded to the aggregated view by the server.
 *
 * Phase 1 of `multi-weld-pqr`: the third arg also accepts an array of
 * weldIds to fetch a multi-weld preview. A single string keeps the
 * legacy single-weld behaviour for callers that haven't migrated.
 */
export async function fetchPqrPreviewData(
  projectId: string,
  pipeSetId: string,
  weldScope?: string | string[] | null
): Promise<PqrPreviewResponse> {
  const token = await getJwtToken()
  if (!token) {
    throw new ApiError(401, 'Not authenticated')
  }

  const base = `${API_BASE_URL}/api/projects/${projectId}/pipe-sets/${pipeSetId}/reports/pqr-preview`
  // Normalise the scope arg into an array of trimmed non-empty ids. A
  // single string survives unchanged; null / undefined / empty arrays
  // collapse to "no scope" so the legacy aggregated view returns.
  const weldIds = Array.isArray(weldScope)
    ? weldScope.map((w) => (typeof w === 'string' ? w.trim() : '')).filter((w) => w.length > 0)
    : weldScope && typeof weldScope === 'string' && weldScope.trim()
      ? [weldScope.trim()]
      : []
  const query =
    weldIds.length > 1
      ? `?weldIds=${encodeURIComponent(weldIds.join(','))}`
      : weldIds.length === 1
        ? `?weldId=${encodeURIComponent(weldIds[0])}`
        : ''
  const url = `${base}${query}`
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }))
    throw new ApiError(res.status, body.error || res.statusText)
  }

  const payload = (await res.json()) as PqrPreviewResponse
  return {
    data: payload.data,
    floorSheets: Array.isArray(payload.floorSheets) ? payload.floorSheets : [],
    version: typeof payload.version === 'number' ? payload.version : 0
  }
}
